import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { cn } from '@/_shared/lib/utils';
import type { InteractiveHeaderStat } from '@/_shared/types';

interface InteractiveCardProps {
  title: React.ReactNode;
  /** Optional leading badge (wizard k/N, or an approval icon tile). */
  leading?: React.ReactNode;
  /** Header-level stats (right-aligned, tabular-nums). */
  stats?: InteractiveHeaderStat[];
  /** When set, renders a dismiss (✕) control in the header. */
  onDismiss?: () => void;
  /** Disable the dismiss control (e.g. while an answer is in flight). */
  dismissDisabled?: boolean;
  /** Card body. */
  children: React.ReactNode;
  /** Footer left slot (Back / Reject / live count). Omit for a right-aligned-only footer. */
  footerLeft?: React.ReactNode;
  /** Footer right slot (Skip / Submit / Approve …). */
  footerRight: React.ReactNode;
  /** Marks the card aria-busy while an action is in flight. */
  busy?: boolean;
  role?: React.AriaRole;
  ariaLabel?: string;
  className?: string;
}

/** Shared shell for every interactive block: header (leading + title + stats + dismiss), body, footer. */
export function InteractiveCard({
  title,
  leading,
  stats,
  onDismiss,
  dismissDisabled,
  children,
  footerLeft,
  footerRight,
  busy,
  role,
  ariaLabel,
  className,
}: InteractiveCardProps): React.ReactElement {
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyId = useId();
  // Collapsed hides the body + footer so the user can read back through the chat before answering; the
  // card still owns the composer slot, since the turn is paused either way. Expanded by default, and
  // never automatic — the block is what's blocking you, so it must not move on its own.
  const [collapsed, setCollapsed] = useState(false);
  // The block takes over the composer mid-turn; move focus into it on appearance so keyboard + screen
  // reader users are brought to the gating ask (the card is role=group + aria-label=prompt, so focusing
  // it announces the question) instead of silently losing focus to <body> when the input unmounts.
  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);
  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className={cn(
        'overflow-hidden rounded-xl border bg-card shadow-sm outline-none',
        // Collapsed, this sits where the resolved answer summary sits and looks much like it — the live
        // border keeps a still-blocking ask from reading as one that's already been answered.
        collapsed ? 'border-primary' : 'border-border',
        className,
      )}
      // A bare <div> doesn't expose aria-label as an accessible name; give it a landmark role when named.
      role={role ?? (ariaLabel ? 'group' : undefined)}
      aria-label={ariaLabel}
      aria-busy={busy}
    >
      <div className="flex items-start gap-2.5 px-4 pt-3.5 pb-2.5">
        {leading}
        {/* Title and stats share a WRAPPING row, so the stats sit beside the prompt while both fit and drop
            to their own line under it when they don't. They cannot simply give way instead: `Badge` is
            `shrink-0 whitespace-nowrap`, so in one unwrapped row the stats hold their full width and the
            prompt absorbs the entire squeeze — narrow enough and it becomes a one-word-per-line column
            beside a bar of chips. The two buttons stay OUTSIDE this group so they stay pinned top-right
            whichever way it wraps. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-2.5 gap-y-1.5">
          <h3
            className={cn(
              // `basis-64` is the wrap threshold: the prompt claims a readable width first, and the stats
              // move down rather than take it. Collapsed there are no stats, so it just fills the row.
              'min-w-0 flex-1 basis-64 pt-0.5 text-[0.9375rem] font-semibold text-foreground',
              // Collapsed is a one-liner: clip the prompt rather than let a long one reflow the bar.
              collapsed ? 'truncate' : 'text-balance',
            )}
          >
            {title}
          </h3>
          {!collapsed && stats && stats.length > 0 && (
            // `flex-wrap` again: once on their own line the chips still cannot shrink, so a set too wide
            // for the card has to stack rather than overflow it.
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
              {stats.map((s, i) => (
                <Badge key={i} variant="outline" className="tabular-nums">
                  {s.value}
                  {s.label ? <span className="font-normal opacity-80">{s.label}</span> : null}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <Button
          variant="tertiary"
          iconOnly
          Icon={collapsed ? <ChevronDown /> : <ChevronUp />}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          onClick={() => setCollapsed((c) => !c)}
        />
        {onDismiss && (
          <Button
            variant="tertiary"
            iconOnly
            Icon={<X />}
            aria-label="Dismiss"
            onClick={onDismiss}
            // Dismiss stays reachable while collapsed: declining needs no reading, accepting does.
            disabled={dismissDisabled}
          />
        )}
      </div>
      {/* `hidden` rather than an unmount, so aria-controls always resolves and the body keeps its scroll
          position across a collapse. In-progress answers live in the question components above this, so
          they survive either way. */}
      <div id={bodyId} hidden={collapsed}>
        {/* Body scrolls (header + footer stay pinned) so a tall question/option list or an expanded
            approval description never pushes Skip/Submit/Approve out of reach — in both the chat composer
            and the narrower audience sidebar. Viewport-relative cap keeps the whole card on screen. */}
        <div className="max-h-[50vh] overflow-y-auto px-1.5 pt-1 pb-1.5">{children}</div>
        <div className="flex items-center justify-between gap-2 border-t border-border px-3.5 py-2.5">
          <div className="flex items-center gap-2">{footerLeft}</div>
          <div className="flex items-center gap-2">{footerRight}</div>
        </div>
      </div>
    </div>
  );
}
